import styled, { keyframes, css } from 'styled-components';

/* ═══════════════════════════════════════════
   GALLERY PAGE STYLES
   Art-gallery feel: image-forward, minimal text,
   masonry layout, spacious dark theme
   ═══════════════════════════════════════════ */

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/* ─── Hero — Shorter, centered ─── */

export const HeroSection = styled.section<{ $bgImage: string }>`
  position: relative;
  height: 60vh;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-image:
    linear-gradient(180deg,
      rgba(10, 22, 40, 0.2) 0%,
      rgba(10, 22, 40, 0.5) 60%,
      var(--bg-primary) 100%),
    url(${({ $bgImage }) => $bgImage});
  background-size: cover;
  background-position: center 40%;
  background-attachment: fixed;

  @media (max-width: 768px) {
    background-attachment: scroll;
    height: 50vh;
    min-height: 340px;
    background-position: center 30%;
  }
`;

export const HeroContent = styled.div`
  max-width: 700px;
  padding: 0 1.5rem;
  animation: ${fadeInUp} 0.8s ease-out;
`;

export const HeroEyebrow = styled.span`
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(10, 22, 40, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 201, 177, 0.3);
  padding: 0.45rem 1.3rem;
  border-radius: 100px;
  margin-bottom: 1.2rem;
`;

export const HeroTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(2.8rem, 6vw, 4.2rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 0.8rem;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);
  line-height: 1.1;

  span {
    background: var(--gradient-accent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.15rem);
  color: var(--text-secondary);
  line-height: 1.7;
  max-width: 500px;
  margin: 0 auto;
`;

/* ─── Filter Bar ─── */

export const FilterSection = styled.div`
  padding: 2.5rem 0 1.5rem;
  background: var(--bg-primary);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 768px) {
    padding: 1.5rem 0 1rem;
  }
`;

export const FilterContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const FilterButton = styled.button<{ $active: boolean }>`
  font-family: var(--font-heading);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 0.6rem 1.6rem;
  border-radius: 100px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 201, 177, 0.5)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${({ $active }) => ($active ? 'rgba(0, 201, 177, 0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--accent-teal)' : 'var(--text-muted)')};
  cursor: pointer;
  transition: all var(--transition-base);
  position: relative;

  ${({ $active }) =>
    $active &&
    css`
      &::after {
        content: '';
        position: absolute;
        bottom: -3px;
        left: 50%;
        transform: translateX(-50%);
        width: 20px;
        height: 2px;
        background: var(--accent-teal);
        border-radius: 1px;
      }
    `}

  &:hover {
    color: var(--accent-teal);
    border-color: rgba(0, 201, 177, 0.3);
    background: rgba(0, 201, 177, 0.06);
  }
`;

/* ─── Masonry Grid ─── */

export const GridSection = styled.section`
  padding: 2.5rem 0 4rem;
  background: var(--bg-primary);
`;

export const GridContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const MasonryGrid = styled.div`
  column-count: 3;
  column-gap: 1.2rem;

  @media (max-width: 1024px) {
    column-count: 2;
  }

  @media (max-width: 576px) {
    column-count: 1;
  }
`;

export const GalleryItem = styled.div<{ $delay: number }>`
  break-inside: avoid;
  margin-bottom: 1.2rem;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  position: relative;
  cursor: pointer;
  animation: ${fadeIn} 0.6s ease-out both;
  animation-delay: ${({ $delay }) => $delay}s;

  &:hover img {
    transform: scale(1.03);
  }

  &:hover > div {
    opacity: 1;
  }
`;

export const GalleryImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
  transition: transform var(--transition-slow);
`;

export const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 40%,
    rgba(0, 0, 0, 0.7) 100%
  );
  opacity: 0;
  transition: opacity var(--transition-base);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.2rem;
`;

export const OverlayCaption = styled.span`
  font-family: var(--font-heading);
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
`;

export const LiveBadge = styled.span`
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-family: var(--font-heading);
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(10, 22, 40, 0.7);
  backdrop-filter: blur(6px);
  padding: 0.3rem 0.6rem;
  border-radius: 100px;
  border: 1px solid rgba(0, 201, 177, 0.3);
`;

/* ─── Loading State ─── */

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

export const LoadingDot = styled.span<{ $delay: number }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--accent-teal);
  margin: 0 0.4rem;
  animation: ${pulse} 1.2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`;

/* ─── Empty State (when filter yields no results) ─── */

export const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 1.5rem;
  color: var(--text-muted);
  font-size: 1rem;
`;

/* ─── Instagram CTA Section ─── */

export const InstagramCTA = styled.section`
  padding: 5rem 0;
  background: var(--bg-secondary);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--accent-teal), transparent);
    opacity: 0.2;
  }
`;

export const CTAContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const CTAHandle = styled.p`
  font-family: var(--font-heading);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.8rem;
`;

export const CTATitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

export const CTAText = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.7;
`;

export const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: var(--accent-teal);
  color: var(--text-inverse);
  padding: 0.85rem 2.2rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  text-decoration: none;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 25px rgba(0, 201, 177, 0.35);
    color: var(--text-inverse);
  }
`;

export const LiveFeedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-heading);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(0, 201, 177, 0.1);
  border: 1px solid rgba(0, 201, 177, 0.25);
  padding: 0.35rem 1rem;
  border-radius: 100px;
  margin-bottom: 1.5rem;
`;

export const PulseDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent-teal);
  animation: ${pulse} 1.5s ease-in-out infinite;
`;
